import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { authHeaders, Toast } from './listPageStyles';
import { StatusBadge, SearchableSelect } from './ui';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  DetailShell, Section, Grid, Icon, SubModal, SplitButton,
  FieldText, FieldDate, FieldSelect, FieldCustom,
} from './detail/DetailShell';
import { printLayout } from './belgeDizayni/printRenderer';
import { usePageActions } from '../contexts/PermissionContext';
import { AllocationModal } from './finance/AllocationModal';
import {
  ALLOCATION_SOURCE_LABELS, normalizeAllocation,
  fmtMoney as fmtTRY, fmtDate as fmtTRDate, toNum,
} from './finance/allocationShared';
import {
  DIRECTION, STATUS, TYPES, GROUP, TypeIcon, getType, listRouteOf,
  instrumentFields, isRequiredField, labelOf,
  PORTFOLIO_STATUS, MANUAL_REASONS, CURRENCIES,
} from './finance/financeTypeMeta';
import { ibanError, normalizeIban, formatIban, formatIbanInput } from '../utils/iban';
import './finance/financeScreens.css';

const API_BASE = '/api/financetransactions';
const TEMPLATE_API = '/api/documenttemplates';
const CUSTOMER_API = '/api/customers';
const REPRESENTATIVE_API = '/api/representatives';
const DOC_TYPE_FINANCE = 3; // Belge Dizaynı: "Tahsilat" tasarımı
const LOOKUP_PAGE = 20;

const getRows = (data) => (Array.isArray(data) ? data : (data?.value || []));

const fmtMoney = (v) =>
  (Number(v) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const toDateInput = (v) => {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
};

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  return undefined;
}
function unwrap(payload) { return pick(payload, 'Data', 'data') ?? payload; }

const lookupLabelOf = (row, nameField) => {
  const code = row.TextId || row.textId || '';
  const name = row[nameField] || row[nameField.toLowerCase()] || row.Name || row.name || '';
  return [code, name].filter(Boolean).join(' — ') || code || '(adsız)';
};

const emptyForm = () => ({
  customerId: '', representativeId: '', amount: '', transactionDate: toDateInput(new Date()),
  documentNo: '', receiptNo: '', description: '', currencyCode: 'TRY',
  bankName: '', chequeBranch: '', bankAccountNo: '', cardLast4: '', installmentCount: '',
  authCode: '', chequeNo: '', drawerName: '', dueDate: '', dbsReference: '',
  portfolioStatus: '', manualReason: '',
});

/** Bir kaydı (camel veya Pascal) forma çevirir. */
const recordToForm = (r) => ({
  ...emptyForm(),
  customerId: pick(r, 'customerId', 'CustomerId') ?? '',
  representativeId: pick(r, 'representativeId', 'RepresentativeId') ?? '',
  amount: pick(r, 'amount', 'Amount') ?? '',
  transactionDate: toDateInput(pick(r, 'transactionDate', 'TransactionDate')),
  documentNo: pick(r, 'documentNo', 'DocumentNo') ?? '',
  receiptNo: pick(r, 'receiptNo', 'ReceiptNo') ?? '',
  description: pick(r, 'description', 'Description') ?? '',
  currencyCode: pick(r, 'currencyCode', 'CurrencyCode') ?? 'TRY',
  bankName: pick(r, 'bankName', 'BankName') ?? '',
  chequeBranch: pick(r, 'chequeBranch', 'ChequeBranch') ?? '',
  // Saklanan biçim boşluksuzdur; ekranda 4'erli gruplanmış gösterilir.
  bankAccountNo: formatIban(pick(r, 'bankAccountNo', 'BankAccountNo') ?? ''),
  cardLast4: pick(r, 'cardLast4', 'CardLast4') ?? '',
  installmentCount: pick(r, 'installmentCount', 'InstallmentCount') ?? '',
  authCode: pick(r, 'authCode', 'AuthCode') ?? '',
  chequeNo: pick(r, 'chequeNo', 'ChequeNo') ?? '',
  drawerName: pick(r, 'drawerName', 'DrawerName') ?? '',
  dueDate: toDateInput(pick(r, 'dueDate', 'DueDate')),
  dbsReference: pick(r, 'dbsReference', 'DbsReference') ?? '',
  portfolioStatus: pick(r, 'portfolioStatus', 'PortfolioStatus') ?? '',
  manualReason: pick(r, 'manualReason', 'ManualReason') ?? '',
});

/**
 * Cari hareket detayı (tahsilat / ödeme) — TAHSILAT_ODEME.md §3.
 *
 * Tahsilat ve Ödeme AYNI bileşendir (simetrik kopya): tek fark yön ve aksan rengidir —
 * Tahsilat yeşil/gelen, Ödeme amber/giden. Kopya-yapıştır iki ayrı ekran TUTULMAZ.
 *
 * Bloklar: aksiyon çubuğu (DetailShell: durum + audit ⓘ + geçmiş ⟳ drawer) → 3 hero kart →
 * tek "Başlık" çerçevesi → TÜRE ÖZEL enstrüman çerçevesi → Eşleşen Faturalar → Belge Özeti →
 * sticky net çubuk.
 *
 * Props: group ('collection' | 'payment') + slug (rota parçası).
 */
export function FinanceTransactionDetailPage({ group, slug }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  const { canAdd, canEdit, canDelete } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;

  const routeType = getType(group, slug);
  const groupMeta = GROUP[group];
  // #831/#832 — Tür ROTADAN gelir ve form içinden DEĞİŞTİRİLEMEZ; hangi türden kayıt açılacağı
  // sol menü ağacından seçilir. `typeKey` yine de state'tir çünkü MEVCUT bir kayıt açılırken
  // ekranı KAYDIN türüne hizalamak gerekir (arama sonucundan gelinmiş olabilir); aksi hâlde
  // yanlış enstrüman çerçevesi çizilirdi. Bkz. load() içindeki hizalama.
  const [typeKey, setTypeKey] = useState(routeType?.key || '');
  useEffect(() => { setTypeKey(routeType?.key || ''); }, [routeType?.key]);
  const type = useMemo(
    () => TYPES.find((t) => t.key === typeKey) || routeType,
    [typeKey, routeType],
  );

  const listRoute = type ? listRouteOf(type) : (groupMeta?.routeBase || '/');
  const fields = type ? instrumentFields(type) : [];
  // Cari işaret GRUPTAN değil YÖNDEN gelir (K3). #827 sonrası ikisi örtüşür (tahsilat grubu
  // azaltır, ödeme grubu artırır) ama kural yine YÖNDÜR: yeni bir tür yanlış gruba düşerse
  // işaret yine doğru kalsın.
  const reducesBalance = type
    ? (type.direction === DIRECTION.collection || type.direction === DIRECTION.creditEntry)
    : true;

  usePageMeta(isNew ? `Yeni ${type?.title || ''}` : `${type?.title || ''} Detayı`,
    type?.subtitle || '');

  const [form, setForm] = useState(emptyForm());
  const [record, setRecord] = useState(null);
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const [toast, setToast] = useState(null);
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [invoiceWarn, setInvoiceWarn] = useState(null);
  const [customerLabel, setCustomerLabel] = useState('');
  const [customerBalance, setCustomerBalance] = useState(null);
  const [repLabel, setRepLabel] = useState('');
  const [allocModal, setAllocModal] = useState(null);

  const showToast = (t, message) => { setToast({ type: t, message }); setTimeout(() => setToast(null), 3500); };
  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));

  const statusNum = pick(record, 'status', 'Status');
  const isCancelled = statusNum === STATUS.cancelled;
  const isReadOnly = !isNew && isCancelled;

  // ── Eşleştirme (yalnız gerçek Tahsilat; Borç Girişi para değildir, fatura kapatmaz) ────────
  const allocations = useMemo(
    () => (pick(record, 'collectionAllocations', 'CollectionAllocations') || []).map(normalizeAllocation),
    [record],
  );
  const txAmount = toNum(pick(record, 'amount', 'Amount'));
  const allocatedTotal = toNum(pick(record, 'allocatedAmount', 'AllocatedAmount'));
  const unallocatedTotal = toNum(pick(record, 'unallocatedAmount', 'UnallocatedAmount'));
  const wasOffline = !!pick(record, 'wasOffline', 'WasOffline');
  const showAllocations = type?.direction === DIRECTION.collection && !isNew && !!record;
  const allocTransaction = useMemo(() => ({
    id: pick(record, 'id', 'Id'),
    customerId: pick(record, 'customerId', 'CustomerId'),
    amount: txAmount,
    documentNo: pick(record, 'documentNo', 'DocumentNo'),
    transactionDate: pick(record, 'transactionDate', 'TransactionDate'),
  }), [record, txAmount]);

  // Faturaların kapanmayan kısmı — "Kalan Açık" (net çubuk + özet).
  const openRemainder = useMemo(
    () => allocations.reduce((sum, a) => sum + Math.max(0, toNum(a.invoice?.openAmount)), 0),
    [allocations],
  );

  // ── Müşteri (tek cari taraf — dört yönde de) ───────────────────────────────────────────────
  const fetchCustomers = useCallback(async (query, skip = 0) => {
    const term = (query || '').replace(/'/g, "''").toLowerCase();
    const filter = term
      ? `contains(tolower(TextId),'${term}') or contains(tolower(Title),'${term}')`
      : '';
    let url = `${CUSTOMER_API}?$top=${LOOKUP_PAGE}&$skip=${skip}&$orderby=TextId`;
    if (filter) url += `&$filter=${encodeURIComponent(filter)}`;
    const r = await fetch(url, { headers: authHeaders() });
    if (!r.ok) return [];
    return getRows(await r.json()).map((c) => ({ value: c.id || c.Id, label: lookupLabelOf(c, 'Title') }));
  }, []);

  // Seçili müşterinin etiketi + GÜNCEL BAKİYESİ.
  //
  // ⚠ Bakiye OData satırındaki `currentBalance` KOLONUNDAN okunmaz: o kolon artık yazılmıyor
  // ve bayat (CARI_BAKIYE_DUZELTME.md B1 — bakiye saklanmaz, iki view'ın toplamıdır).
  // Doğru kaynak `/api/customers/{id}/balance` ucudur; etiket için OData sorgusu kalır.
  const resolveCustomer = useCallback(async (custId) => {
    if (!custId) { setCustomerLabel(''); setCustomerBalance(null); return; }
    try {
      const url = `${CUSTOMER_API}?$filter=${encodeURIComponent(`Id eq ${custId}`)}&$top=1`;
      const r = await fetch(url, { headers: authHeaders() });
      if (r.ok) {
        const rows = getRows(await r.json());
        if (rows[0]) setCustomerLabel(lookupLabelOf(rows[0], 'Title'));
      }
    } catch { /* etiket çözülemedi — bakiye yine de denenir */ }

    try {
      const b = await fetch(`${CUSTOMER_API}/${custId}/balance`, { headers: authHeaders() });
      if (!b.ok) { setCustomerBalance(null); return; }
      const json = await b.json();
      const data = json?.data ?? json?.Data ?? json;
      const bal = data?.balance ?? data?.Balance;
      setCustomerBalance(bal == null ? null : Number(bal));
    } catch { setCustomerBalance(null); }
  }, []);

  const fetchRepresentatives = useCallback(async (query, skip = 0) => {
    const term = (query || '').replace(/'/g, "''").toLowerCase();
    const filter = term
      ? `contains(tolower(TextId),'${term}') or contains(tolower(Name),'${term}')`
      : '';
    let url = `${REPRESENTATIVE_API}?$top=${LOOKUP_PAGE}&$skip=${skip}&$orderby=TextId`;
    if (filter) url += `&$filter=${encodeURIComponent(filter)}`;
    const r = await fetch(url, { headers: authHeaders() });
    if (!r.ok) return [];
    return getRows(await r.json()).map((c) => ({ value: c.id || c.Id, label: lookupLabelOf(c, 'Name') }));
  }, []);

  const resolveRepLabel = useCallback(async (repId) => {
    if (!repId) { setRepLabel(''); return; }
    try {
      const url = `${REPRESENTATIVE_API}?$filter=${encodeURIComponent(`Id eq ${repId}`)}&$top=1`;
      const r = await fetch(url, { headers: authHeaders() });
      if (!r.ok) return;
      const rows = getRows(await r.json());
      if (rows[0]) setRepLabel(lookupLabelOf(rows[0], 'Name'));
    } catch { /* yoksay */ }
  }, []);

  const resolveLabels = useCallback((f) => {
    resolveCustomer(f.customerId);
    resolveRepLabel(f.representativeId);
  }, [resolveCustomer, resolveRepLabel]);

  // ── Kayıt yükleme ─────────────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (isNew) {
      setLoading(false);
      if (location.state?.clone) {
        const f = location.state.clone;
        setForm(f);
        setTried(false);
        resolveLabels(f);
      }
      return;
    }
    const stateRow = location.state?.row;
    if (stateRow) {
      setRecord(stateRow);
      const f = recordToForm(stateRow);
      setForm(f);
      resolveLabels(f);
    }
    setLoading(!stateRow);
    try {
      const res = await fetch(`${API_BASE}/GetById?Id=${id}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Kayıt alınamadı (${res.status})`);
      const r = unwrap(await res.json());
      if (r) {
        setRecord(r);
        const f = recordToForm(r);
        setForm(f);
        resolveLabels(f);
        setAudit({
          createdBy: pick(r, 'createdBy', 'CreatedBy'),
          createdDate: pick(r, 'createdDate', 'CreatedDate'),
          updatedBy: pick(r, 'updatedBy', 'UpdatedBy'),
          updatedDate: pick(r, 'updatedDate', 'UpdatedDate'),
        });
        // Kaydın kendi tür/yönü rotadan farklı olabilir (ör. arama sonucundan gelindi):
        // ekranı KAYDIN türüne hizala, yoksa yanlış enstrüman çerçevesi çizilirdi.
        const dir = pick(r, 'direction', 'Direction');
        const mth = pick(r, 'method', 'Method');
        const match = TYPES.find((t) => t.direction === dir && t.method === mth);
        if (match) setTypeKey(match.key);
      }
    } catch (err) {
      if (!stateRow) showToast('error', err.message || 'Kayıt alınamadı');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew, resolveLabels]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (isNew && location.state?.clone) {
      const f = location.state.clone;
      setForm(f);
      setTried(false);
      resolveLabels(f);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // ── Kaydet ────────────────────────────────────────────────────────────────────────────────
  // Yalnız SEÇİLİ TÜRÜN enstrüman alanları gönderilir; tür değiştiyse eski türün alanları
  // null'lanır (§8: gizlenen alan DOM'dan çıkar, veriden de çıkmalı — aksi halde Nakit'e
  // çevrilen bir çek kaydında çek no/vade sessizce asılı kalırdı).
  const buildPayload = () => {
    const on = (k) => fields.includes(k);
    const num = (k) => (form[k] === '' || form[k] == null ? null : Number(form[k]));
    return {
      direction: type.direction,
      method: type.method,
      status: STATUS.active,
      customerId: form.customerId || null,
      representativeId: form.representativeId || null,
      amount: Number(form.amount),
      transactionDate: form.transactionDate ? new Date(form.transactionDate).toISOString() : new Date().toISOString(),
      // Boş gönderilir → sunucu fiş numarasını ÜRETİR (yeni kayıt). Mevcut kayıtta dolu gider ve
      // sunucu onu korur; numara bir kez verildikten sonra değişmez.
      documentNo: form.documentNo || null,
      description: form.description || null,
      currencyCode: on('currencyCode') ? (form.currencyCode || 'TRY') : 'TRY',
      bankName: on('bankName') ? (form.bankName || null) : null,
      chequeBranch: on('chequeBranch') ? (form.chequeBranch || null) : null,
      // Boşluksuz/büyük harf gönderilir: "TR33 0006 …" ile "TR330006…" aynı hesap; iki farklı
      // string olarak saklanırsa arama ve mükerrer kontrolü kaçırır. Sunucu da normalleştirir,
      // ama gönderilen değerin kullanıcının gördüğüyle aynı olması teşhisi kolaylaştırır.
      bankAccountNo: on('bankAccountNo') ? (normalizeIban(form.bankAccountNo) || null) : null,
      cardLast4: on('cardLast4') ? (form.cardLast4 || null) : null,
      installmentCount: on('installmentCount') ? num('installmentCount') : null,
      authCode: on('authCode') ? (form.authCode || null) : null,
      chequeNo: on('chequeNo') ? (form.chequeNo || null) : null,
      drawerName: on('drawerName') ? (form.drawerName || null) : null,
      dueDate: on('dueDate') && form.dueDate ? new Date(form.dueDate).toISOString() : null,
      dbsReference: on('dbsReference') ? (form.dbsReference || null) : null,
      portfolioStatus: on('portfolioStatus') ? num('portfolioStatus') : null,
      manualReason: on('manualReason') ? (form.manualReason || null) : null,
    };
  };

  const persist = async ({ andNew = false } = {}) => {
    setTried(true);
    if (!form.customerId) { showToast('error', 'Müşteri seçilmelidir'); return; }
    const amount = Number(form.amount);
    if (!amount || amount <= 0) { showToast('error', 'Tutar sıfırdan büyük olmalı'); return; }
    const missing = fields.filter((f) => isRequiredField(type, f) && f !== 'amount'
      && !String(form[f] ?? '').trim());
    if (missing.length) {
      showToast('error', `Zorunlu alan boş: ${missing.map((f) => labelOf(type, f)).join(', ')}`);
      return;
    }
    // Bozuk IBAN ile kaydetmeyi ENGELLE. Sunucu da reddeder (aynı kural), ama kullanıcıya
    // hatayı ağ turu beklemeden, alanın altında göstermek gerekiyor.
    const ibanMsg = fields.includes('bankAccountNo') ? ibanError(form.bankAccountNo) : null;
    if (ibanMsg) { showToast('error', ibanMsg); return; }

    setSaving(true);
    try {
      const payload = buildPayload();
      const res = await fetch(`${API_BASE}/${isNew ? 'Add' : 'Update'}`, {
        method: isNew ? 'POST' : 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(isNew ? payload : { id: pick(record, 'id', 'Id'), ...payload }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || (isNew ? 'Kayıt eklenemedi' : 'Kayıt güncellenemedi'));
      if (isNew && andNew) {
        showToast('success', 'Kayıt eklendi. Yeni kayda devam edebilirsiniz.');
        setForm(emptyForm());
        setCustomerLabel('');
        setCustomerBalance(null);
        setRepLabel('');
        setTried(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      showToast('success', isNew ? 'Kayıt başarıyla eklendi' : 'Kayıt başarıyla güncellendi');
      // Tür değiştirilmiş olabilir → kaydın YENİ türünün listesine dön.
      setTimeout(() => navigate(listRoute), 400);
    } catch (err) {
      showToast('error', err.message || 'İşlem sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => persist();
  const handleClone = () => navigate(`${listRoute}/new`, { state: { clone: { ...form } } });

  // ── İptal (K7: onaylı soft iptal; telafi/ters kayıt yazılmaz, etki geri alınır) ────────────
  const handleCancel = async (confirmed = false) => {
    const recId = pick(record, 'id', 'Id');
    if (!recId) return;
    setSaving(true);
    try {
      const qs = confirmed ? '?confirmed=true' : '';
      const res = await fetch(`${API_BASE}/Cancel${qs}`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id: recId }),
      });
      const data = await res.json().catch(() => null);
      const payload = data?.data || data?.Data;
      if (res.status === 409 && (payload?.code || payload?.Code) === 'FATURA_BAGLANTISI_ONAYI_GEREKLI') {
        setCancelConfirm(false);
        setInvoiceWarn(payload.invoices || payload.Invoices || []);
        return;
      }
      if (!res.ok) throw new Error(data?.message || data?.Message || 'İşlem iptal edilemedi');
      showToast('success', 'Hareket iptal edildi');
      setCancelConfirm(false);
      setInvoiceWarn(null);
      load();
    } catch (err) {
      showToast('error', err.message || 'İptal sırasında hata oluştu');
      setCancelConfirm(false);
      setInvoiceWarn(null);
    } finally {
      setSaving(false);
    }
  };

  // ── Yazdırma / Makbuz(Dekont) — ikisi de Belge Dizaynı DocType 3 şablonunu kullanır ────────
  const handlePrint = async () => {
    const recId = pick(record, 'id', 'Id');
    if (!recId) { showToast('error', 'Önce kaydı yükleyin'); return; }
    try {
      const tRes = await fetch(
        `${TEMPLATE_API}?$filter=${encodeURIComponent(`DocType eq ${DOC_TYPE_FINANCE} and IsActive eq true`)}&$top=25`,
        { headers: authHeaders() },
      );
      const tpls = tRes.ok ? getRows(await tRes.json()) : [];
      const tpl = tpls.find((t) => t.isDefault || t.IsDefault) || tpls[0];
      const layoutJson = tpl && (tpl.layoutJson || tpl.LayoutJson);
      if (!layoutJson) {
        showToast('error', "Yazdırmak için Belge Dizaynı'nda 'Tahsilat' türünde bir şablon oluşturup kaydedin.");
        return;
      }
      const rRes = await fetch(`${TEMPLATE_API}/ResolveData?docType=${DOC_TYPE_FINANCE}&documentId=${recId}`, { headers: authHeaders() });
      if (!rRes.ok) { showToast('error', 'Belge verisi alınamadı — sunucunun güncel sürümü çalışıyor mu?'); return; }
      const json = await rRes.json();
      const data = json?.data ?? json?.Data ?? json;
      printLayout(JSON.parse(layoutJson), data, type.title);
    } catch (err) {
      showToast('error', err.message || 'Yazdırma sırasında hata oluştu');
    }
  };

  if (!type) {
    return <div style={{ padding: 24 }}>Tanımsız finans türü: {group}/{slug}</div>;
  }

  const statusBadge = !isNew && (isCancelled
    ? <StatusBadge tone="neutral">İptal</StatusBadge>
    : <StatusBadge tone="ok">İşlendi</StatusBadge>);

  const actions = (
    <>
      {!isNew && !isCancelled && canDelete && (
        <button className="dps-btn danger" onClick={() => setCancelConfirm(true)} disabled={saving}>
          <Icon name="x" size={16} /> İptal Et
        </button>
      )}
      {!isNew && <button className="dps-btn ghost" onClick={handlePrint} disabled={saving}>Yazdır</button>}
      {!isNew && <button className="dps-btn ghost" onClick={handlePrint} disabled={saving}>{groupMeta.receiptLabel}</button>}
      <button className="dps-btn ghost" onClick={() => navigate(listRoute)} disabled={saving}>
        {isReadOnly ? 'Geri' : 'İptal'}
      </button>
      {!isReadOnly && canSave && (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={isNew
            ? [{ label: 'Kaydet ve Yeni', desc: 'Kaydet, listeye dönme, yeni kayıt aç', icon: 'plus', onClick: () => persist({ andNew: true }) }]
            : [{ label: 'Klonla', desc: 'Bu kaydı kopyalayıp yeni kayıt aç', icon: 'copy', onClick: handleClone }]}
        />
      )}
    </>
  );

  // ── Enstrüman alanı çizici — türe göre alanlar DOM'a girer/çıkar ──────────────────────────
  const renderField = (f) => {
    const label = labelOf(type, f);
    const req = isRequiredField(type, f);
    const key = f;
    if (f === 'dueDate') return <FieldDate key={key} form={form} set={set} tried={tried} k={f} label={label} req={req} />;
    if (f === 'currencyCode') return <FieldSelect key={key} form={form} set={set} k={f} label={label} options={CURRENCIES} />;
    if (f === 'portfolioStatus') {
      return (
        <FieldSelect key={key} form={form} set={set} k={f} label={label}
                     options={[{ value: '', label: '— seçilmedi —' }, ...PORTFOLIO_STATUS]} />
      );
    }
    if (f === 'manualReason') {
      return (
        <FieldSelect key={key} form={form} set={set} tried={tried} k={f} label={label} req={req}
                     options={[{ value: '', label: '— seçiniz —' }, ...MANUAL_REASONS]} />
      );
    }
    if (f === 'amount') {
      return (
        <FieldText key={key} form={form} set={set} tried={tried} k={f} label={label} req={req}
                   num type="number" suffix="₺" />
      );
    }
    if (f === 'installmentCount') {
      return <FieldText key={key} form={form} set={set} k={f} label={label} num type="number" placeholder="1 = tek çekim" />;
    }
    // "Hesap Numarası / IBAN" — alan İKİSİNİ DE kabul eder, bu yüzden doğrulama KOŞULLUDUR:
    // değer IBAN olma iddiasındaysa (2 harf + 2 rakam ile başlıyorsa) kontrol basamağı
    // doğrulanır; düz hesap numarası serbest kalır. Aynı kural sunucuda da uygulanır
    // (Middleware/Finance/Iban.cs) — ikisi ayrışırsa ekran "geçerli" derken sunucu reddederdi.
    if (f === 'bankAccountNo') {
      return (
        <FieldText
          key={key} form={form} set={set} tried={tried} k={f} label={label} req={req} mono
          format={formatIbanInput}
          error={ibanError(form.bankAccountNo)}
          hint="IBAN ya da düz hesap numarası girilebilir. IBAN girilirse 4'erli gruplanır ve kontrol basamağı doğrulanır."
          placeholder="TR33 0006 1005 1978 6457 8413 26"
        />
      );
    }
    if (f === 'description') {
      return <FieldText key={key} form={form} set={set} k={f} label={label} span />;
    }
    return <FieldText key={key} form={form} set={set} tried={tried} k={f} label={label} req={req} />;
  };

  const heroTone = groupMeta.heroTone; // Tahsilat yeşil/gelen · Ödeme amber/giden
  const balanceText = customerBalance == null ? '—' : `₺ ${fmtMoney(customerBalance)}`;

  return (
    <DetailShell
      crumb={[{ label: type.title, to: listRoute }, { label: isNew ? 'Yeni Kayıt' : 'Detay', current: true }]}
      status={statusBadge}
      audit={isNew ? null : audit}
      history={isNew ? null : { entityName: 'FinanceTransaction', entityId: id }}
      actions={actions}
      loading={loading}
    >
      {/* §3 — 3 hero kart: Tutar · Eşleşen Fatura · Müşteri Bakiye */}
      <div className="fin-hero">
        <div className={`fin-hcard fin-hcard--${heroTone}`}>
          <div className="fin-hl"><TypeIcon name={type.icon} size={16} />{groupMeta.word} Tutarı</div>
          <div className="fin-hv">₺ {fmtMoney(form.amount)}</div>
          <div className="fin-hm">
            {type.typeLabel} · {form.currencyCode || 'TRY'}
            {form.transactionDate ? ` · ${fmtTRDate(form.transactionDate)}` : ''}
          </div>
        </div>
        <div className="fin-hcard">
          <div className="fin-hl"><Icon name="list" size={16} />Eşleşen Fatura</div>
          <div className="fin-hv">{showAllocations ? `₺ ${fmtMoney(allocatedTotal)}` : '—'}</div>
          <div className="fin-hm">
            {showAllocations
              ? `${allocations.length} fatura · ${unallocatedTotal > 0 ? `₺ ${fmtMoney(unallocatedTotal)} dağıtılmamış` : 'tam dağıtıldı'}`
              : 'Bu türde fatura eşleştirmesi yapılmaz'}
          </div>
        </div>
        <div className="fin-hcard">
          <div className="fin-hl"><Icon name="wallet" size={16} />Müşteri Bakiye</div>
          <div className="fin-hv">{balanceText}</div>
          <div className="fin-hm">
            {customerBalance == null ? 'Müşteri seçilmedi'
              : (customerBalance > 0 ? 'Kalan alacak' : (customerBalance < 0 ? 'Müşteri alacaklı' : 'Borç kapandı'))}
          </div>
        </div>
      </div>

      <div className="dps-form">
        {/* §3 — tek "Başlık" çerçevesi. Eski ayrı Belge + Müşteri çerçeveleri BİRLEŞTİRİLDİ;
            "Tip" ve "Bölge / Rut" alanları KALDIRILDI (2026-07-22). */}
        <Section
          icon="hash"
          title="Başlık"
          subtitle={`${groupMeta.section} · belge ve müşteri künyesi`}
          note={isCancelled ? 'Bu hareket iptal edilmiştir; üzerinde değişiklik yapılamaz.' : undefined}
        >
          <Grid cols={3}>
            {/* Fiş no SUNUCU ÜRETİR (THS-2026-000842 / ODE-…): kayıt anında verilir ve kaydın
                kimliğidir. Yeni kayıtta boş görünür — elle numara uydurmak, seriyi ve makbuz
                eşleştirmesini bozacağı için alan salt-okunurdur. */}
            <FieldText form={form} set={set} k="documentNo" label="Belge No" readOnly
                       placeholder={isNew ? 'Kaydedilince otomatik verilir' : undefined} />
            <FieldDate form={form} set={set} tried={tried} k="transactionDate" label={`${groupMeta.word} Tarihi`} req />
            {/* #831/#832 — SEÇİM alanı kaldırıldı. Tür SOL MENÜDEN seçilir ve rotadan gelir;
                form içinden değiştirilebilmesi "Nakit Tahsilat" menüsünden çek kaydı açmaya
                izin veriyordu. Alan künye olarak salt-okunur kalır. */}
            <FieldText
              form={{ typeLabel: type?.typeLabel || '-' }} set={() => {}} k="typeLabel"
              label={`${groupMeta.word} Türü`} readOnly
            />
            <FieldCustom label="Müşteri (kod — ünvan)" req invalid={tried && !form.customerId} span>
              <SearchableSelect
                value={form.customerId}
                selectedLabel={customerLabel}
                onChange={(v) => { set('customerId', v); resolveCustomer(v); }}
                fetchOptions={fetchCustomers}
                disabled={isReadOnly}
                placeholder="Müşteri ara…"
                pageSize={LOOKUP_PAGE}
              />
            </FieldCustom>
            <FieldText form={{ balance: balanceText }} set={() => {}} k="balance" label="Güncel Bakiye" readOnly />
            <FieldCustom label="Temsilci" span>
              <SearchableSelect
                value={form.representativeId}
                selectedLabel={repLabel}
                onChange={(v) => { set('representativeId', v); resolveRepLabel(v); }}
                fetchOptions={fetchRepresentatives}
                disabled={isReadOnly}
                placeholder="Temsilci ara…"
                pageSize={LOOKUP_PAGE}
              />
            </FieldCustom>
          </Grid>
        </Section>

        {/* §3 — TÜRE ÖZEL enstrüman çerçevesi. Nakit ve manuel girişte hiçbir banka alanı yoktur. */}
        <Section
          icon={type.instrument === 'manual' ? 'info' : 'wallet'}
          title={type.instrument === 'manual' ? 'Manuel Kayıt' : `${type.typeLabel} Detayı`}
          subtitle={type.subtitle}
        >
          <Grid cols={3}>{fields.map(renderField)}</Grid>
        </Section>

        {/* §3 — Eşleşen Faturalar (yalnız gerçek tahsilat) */}
        {showAllocations && (
          <Section
            icon="list"
            title="Eşleşen Faturalar"
            subtitle={`Bu ${groupMeta.word.toLowerCase()}in kapattığı açık faturalar`}
            note={wasOffline
              ? 'Bu tahsilat cihazda ÇEVRİMDIŞI alındı — eşleştirmesi bayat olabilir, gözden geçirin.'
              : undefined}
          >
            {allocations.length === 0 ? (
              <div className="fin-empty">Bu tahsilat henüz faturaya eşleştirilmemiş.</div>
            ) : (
              <div className="fin-table-wrap">
                <table className="fin-table">
                  <thead>
                    <tr>
                      <th>Fatura No</th><th>Tarih</th><th>Vade</th>
                      <th className="n">Tutar</th><th className="n">Kapanan</th><th className="n">Kalan</th>
                      <th>Kaynak</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allocations.map((a, i) => (
                      <tr key={a.id || `${a.invoiceId}-${i}`}>
                        <td>
                          <button type="button" className="fin-inv-link"
                                  onClick={() => navigate(`/invoices/${a.invoiceId}`)}>
                            {a.invoice?.invoiceNo || 'Fatura'}
                          </button>
                        </td>
                        <td>{fmtTRDate(a.invoice?.issuedAt)}</td>
                        <td>{fmtTRDate(a.invoice?.dueDate)}</td>
                        <td className="n">{fmtTRY(a.invoice?.netAmount)}</td>
                        <td className="n">{fmtTRY(a.allocatedAmount)}</td>
                        <td className="n">{fmtTRY(a.invoice?.openAmount)}</td>
                        <td>{ALLOCATION_SOURCE_LABELS[a.allocatedBy] || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div style={{ maxWidth: 340, marginLeft: 'auto', marginTop: 12 }}>
              <div className="fin-kv"><span>{groupMeta.word} Tutarı</span><span>{fmtTRY(txAmount)}</span></div>
              <div className="fin-kv"><span>Kapatılan</span><span>{fmtTRY(allocatedTotal)}</span></div>
              <div className="fin-kv"><span>Dağıtılmamış (avans)</span><span>{fmtTRY(unallocatedTotal)}</span></div>
            </div>

            {canEdit && !isCancelled && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                {unallocatedTotal > 0 && (
                  <button type="button" className="dps-btn ghost" onClick={() => setAllocModal({ mode: 'allocate' })}>
                    Faturaya Eşle
                  </button>
                )}
                {allocations.length > 0 && (
                  <button type="button" className="dps-btn ghost" onClick={() => setAllocModal({ mode: 'reallocate' })}>
                    Yeniden Eşleştir (Düzeltme)
                  </button>
                )}
              </div>
            )}
          </Section>
        )}

        {/* §3 — Belge Özeti. Kasa / banka hesabı / muhasebe fişi satırı YOKTUR ve açıklayıcı
            not gösterilmez; bu, sonraki iterasyonlarda "eksik" sanılıp geri eklenmemesi gereken
            BİLİNÇLİ bir sadeleştirmedir. */}
        {!isNew && (
          <Section icon="info" title="Belge Özeti" subtitle="Cari hareket bilgileri">
            <Grid cols={2}>
              <div>
                <div className="fin-kv"><span>{groupMeta.word} Türü</span><span>{type.typeLabel}</span></div>
                {type.hasDueDate && (
                  <div className="fin-kv"><span>Vade</span><span>{form.dueDate ? fmtTRDate(form.dueDate) : '—'}</span></div>
                )}
                {/* §3 — Belge Özeti'nde YALNIZ Cari Hareket ve Makbuz/Dekont referansları bulunur.
                    Cari hareket AYRI bir kayıt değildir: bu belgenin KENDİSİ cari harekettir ve
                    fiş numarası onun referansıdır. Uydurma bir "CH-…" numarası üretmek, karşılığı
                    olmayan bir belgeye işaret etmek olurdu. */}
                <div className="fin-kv">
                  <span>Cari Hareket</span>
                  <span>{form.documentNo || '—'}</span>
                </div>
                <div className="fin-kv">
                  <span>{groupMeta.receiptLabel}</span>
                  <span>
                    {form.receiptNo
                      || (type.instrument === 'manual' ? 'Manuel girişte düzenlenmez' : '—')}
                  </span>
                </div>
              </div>
              <div>
                <div className="fin-kv"><span>Para Birimi</span><span>{form.currencyCode || 'TRY'}</span></div>
                <div className="fin-kv"><span>Cari Etki</span><span>{reducesBalance ? 'Borç azalır (−)' : 'Borç artar (+)'}</span></div>
                <div className="fin-kv"><span>Kaynak</span><span>{wasOffline ? 'Mobil · çevrimdışı' : 'Çevrimiçi'}</span></div>
              </div>
            </Grid>
          </Section>
        )}
      </div>

      {/* §3 — sticky net çubuk */}
      <div className={`fin-netbar fin-netbar--${heroTone}`}>
        <div className="fin-it">{groupMeta.word}<b>₺ {fmtMoney(form.amount)}</b></div>
        <div className="fin-it">Eşleşen<b>{showAllocations ? `₺ ${fmtMoney(allocatedTotal)}` : '—'}</b></div>
        <div className="fin-it">Kalan Açık<b>{showAllocations ? `₺ ${fmtMoney(openRemainder)}` : '—'}</b></div>
        <div className="fin-net">
          <div className="fin-net-l">Müşteri Bakiye (işlem sonrası)</div>
          <div className="fin-net-v">{balanceText}</div>
        </div>
      </div>

      <AllocationModal
        open={!!allocModal}
        mode={allocModal?.mode || 'allocate'}
        transaction={allocTransaction}
        existingAllocations={allocations}
        onClose={() => setAllocModal(null)}
        onError={(msg) => showToast('error', msg)}
        onSaved={(_r, info) => {
          setAllocModal(null);
          showToast('success', `Eşleştirme kaydedildi — Dağıtılan ${fmtTRY(info.allocated)} · Avans ${fmtTRY(info.unallocated)}`);
          load();
        }}
      />

      {cancelConfirm && (
        <SubModal
          title="İptal Onayı"
          maxWidth={440}
          onClose={() => setCancelConfirm(false)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setCancelConfirm(false)} disabled={saving}>Vazgeç</button>
              <button className="dps-btn danger" onClick={() => handleCancel()} disabled={saving}>
                {saving ? 'İşleniyor…' : 'Evet, İptal Et'}
              </button>
            </>
          )}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            Bu hareketi iptal etmek istediğinize emin misiniz? Müşteri bakiyesine olan etkisi geri alınacaktır.
          </p>
        </SubModal>
      )}

      {invoiceWarn && (
        <SubModal
          title="Faturaya Bağlı Tahsilat"
          maxWidth={520}
          onClose={() => setInvoiceWarn(null)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setInvoiceWarn(null)} disabled={saving}>Hayır</button>
              <button className="dps-btn danger" onClick={() => handleCancel(true)} disabled={saving}>
                {saving ? 'İşleniyor…' : 'Evet, Devam Et'}
              </button>
            </>
          )}
        >
          <p style={{ margin: '0 0 10px', lineHeight: 1.6 }}>
            Bu tahsilat aşağıdaki fatura(lar)a bağlıdır. Tahsilat iptal edilirse faturanın ödemesi de
            geri alınacaktır (fatura yeniden açık borç olur). Devam etmek istiyor musunuz?
          </p>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {invoiceWarn.map((iv, i) => (
              <li key={i} style={{ marginBottom: 4 }}>
                {(iv.textId || iv.TextId) ? `${iv.textId || iv.TextId} — ` : ''}
                {Number(iv.allocatedAmount ?? iv.AllocatedAmount ?? 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
              </li>
            ))}
          </ul>
        </SubModal>
      )}

      <Toast toast={toast} />
    </DetailShell>
  );
}

export default FinanceTransactionDetailPage;
